import { EmptyState } from '@/components/ui/EmptyState'
import { Spinner } from '@/components/ui/Spinner'
import { useUrlFilters } from '@/hooks/useUrlFilters'
import { Box, HStack, Heading, VStack } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { UsersFilters } from '../components/UsersFilters'
import { UsersTable } from '../components/UsersTable'
import { useUsers } from '../hooks/useUsers'

/**
 * UsersList Page Component
 * Displays list of all system users (superadmin only)
 */
export function UsersList() {
  const { t } = useTranslation()

  // Use URL filters hook
  const { filters, updateFilters, handlePaginationChange, handleSortingChange } = useUrlFilters({
    search: '',
    is_active: '',
    role: '',
    page: 1,
    page_size: 20,
    ordering: '-date_joined',
  })

  // Build API params
  const params = {
    page: filters.page,
    page_size: filters.page_size,
    ordering: filters.ordering,
    ...(filters.search && { search: filters.search }),
    ...(filters.is_active && { is_active: filters.is_active === 'true' }),
    ...(filters.role === 'superadmin' && { is_superuser: true }),
    ...(filters.role === 'staff' && { is_staff: true }),
  }

  // Fetch users
  const { data, isLoading, error } = useUsers(params)

  const users = data?.data || []
  const pagination = data?.meta?.pagination || {}
  const totalCount = pagination.total_count || 0
  const pageCount = Math.ceil(totalCount / filters.page_size)

  const handleFiltersChange = (newFilters) => {
    updateFilters(newFilters)
  }

  const handleResetFilters = () => {
    updateFilters({
      search: '',
      is_active: '',
      role: '',
      page: 1,
      ordering: '-date_joined',
    })
  }

  if (isLoading) {
    return <Spinner fullScreen />
  }

  if (error) {
    return (
      <EmptyState
        title={t('users.title')}
        description={error.message || t('errors.serverErrorDesc')}
      />
    )
  }

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" flexWrap="wrap" gap={4}>
          <Box>
            <Heading size="lg" color="text.primary">{t('users.title')}</Heading>
          </Box>
        </HStack>

        {/* Filters */}
        <UsersFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onReset={handleResetFilters}
        />

        {/* Table */}
        <UsersTable
          data={users}
          isLoading={isLoading}
          pageCount={pageCount}
          totalCount={totalCount}
          pagination={{
            pageIndex: filters.page - 1,
            pageSize: filters.page_size,
          }}
          sorting={filters.ordering ? [{
            id: filters.ordering.startsWith('-') ? filters.ordering.slice(1) : filters.ordering,
            desc: filters.ordering.startsWith('-')
          }] : []}
          onPaginationChange={handlePaginationChange}
          onSortingChange={handleSortingChange}
        />
      </VStack>
    </Box>
  )
}

export default UsersList
